/**
 * Auth Guard
 *
 * Protects routes from unauthorized access.
 * Redirects to /login if not authenticated.
 * Properly waits for auth loading to complete before making decisions.
 */

import { inject } from '@angular/core';
import { Router, CanActivateFn, UrlTree } from '@angular/router';
import { AuthService } from './auth.service';

export const authGuard: CanActivateFn = (): Promise<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Return a promise that resolves when loading completes
  return new Promise<boolean | UrlTree>((resolve) => {
    const checkAuth = () => {
      if (authService.isLoading()) {
        // Still loading, re-check after a short delay
        setTimeout(checkAuth, 50);
        return;
      }

      if (authService.isAuthenticated()) {
        resolve(true);
      } else {
        // Not authenticated, redirect to login
        resolve(router.createUrlTree(['/login']));
      }
    };

    checkAuth();
  });
};

/**
 * Role Guard Factory
 *
 * Creates a guard that checks for a specific role.
 */
export function roleGuard(requiredRole: 'viewer' | 'agent' | 'admin'): CanActivateFn {
  return () => {
    const authService = inject(AuthService);
    const router = inject(Router);

    if (!authService.isAuthenticated()) {
      router.navigate(['/login']);
      return false;
    }

    if (!authService.hasRole(requiredRole)) {
      // User doesn't have the required role
      // Redirect to home or show unauthorized page
      router.navigate(['/']);
      return false;
    }

    return true;
  };
}
